import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Card from "./Card";
import { useEffect } from "react";
import axiosInstance from "../../utils/axiosConfig";
import { useContext, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import ChatIcon from "../../components/ChatIcon";
import EmergencyAgent from "../../components/ElevenLabs/EmergencyAgent";
import { AuthContext } from "../../Context/AuthContext";

// Load ElevenLabs ConvAI widget script
const loadElevenLabsWidget = () => {
  if (document.getElementById('elevenlabs-convai-script')) return;
  const script = document.createElement('script');
  script.id = 'elevenlabs-convai-script';
  script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
  script.async = true;
  script.type = 'text/javascript';
  document.body.appendChild(script);
};

const DASHBOARD_CARDS = [
  { permissionKey: "PROFILE", searchLabel: "Profile", title: "Profile", icon: "/cards/profile.png", path: "/profile" },
  { permissionKey: "VITALS", searchLabel: "Vitals", title: "Vitals", icon: "/cards/patient.png", path: "/vitals" },
  { permissionKey: "FAQS", searchLabel: "FAQs", title: "FAQs", icon: "/cards/nurse.png", path: "/ai-agent" },
  { permissionKey: "PATIENT_REGISTRATION", searchLabel: "Patient Registration", title: "Patient Registration", icon: "/cards/profile.png", path: "/patient-registration" },
  { permissionKey: "NURSE_CHAT", searchLabel: "Assessments", title: "Assessments", icon: "/cards/voice.png", path: "/dashboard/nurse-chat" },
  { permissionKey: "SCHEDULER", searchLabel: "Appointments", title: "Appointments", icon: "/cards/pain.png", path: "/appointments" },
  { permissionKey: "VISUAL_DATA", searchLabel: "Visuals Data", title: "Visuals Data", icon: "/cards/visual.png", path: "/visual-data" },
  { permissionKey: "MEDS_ADH", searchLabel: "Medications", title: "Medications", icon: "/cards/medications.png", path: "/medications" },
  { permissionKey: "SHARE_DATA", searchLabel: "Share Data", title: "Share Data", icon: "/cards/share.png", path: "/dashboard/share-data" },
  { permissionKey: "PAIN_MNMNT", searchLabel: "Pain Data", title: "Pain Data", icon: "/cards/pain.png", path: "/pain-management" },
];

const MainDashboard = () => {
  const navigate = useNavigate();
  const { user: ctxUser, isAuthenticated, loading: authLoading } = useContext(AuthContext);
  const [accessList, setAccessList] = useState([]);
  const [permLoading, setPermLoading] = useState(true);
  // Load ElevenLabs ConvAI widget on mount
  useEffect(() => {
    loadElevenLabsWidget();
  }, []);

  // Fetch user permissions
  useEffect(() => {
    // Get user from localStorage or context
    const storedUserStr = localStorage.getItem("user");
    let effectiveUser = ctxUser;
    if (!effectiveUser && storedUserStr) {
      try { effectiveUser = JSON.parse(storedUserStr); } catch(e) { /* noop */ }
    }

    const token = localStorage.getItem("token") || localStorage.getItem("dToken");
    if (!token) {
      console.log("No token found, redirecting to login");
      navigate("/login");
      return;
    }

    const userId = effectiveUser?._id;

    // Try cached permissions for instant UI while fetching fresh
    if (userId) {
      const cached = localStorage.getItem(`permissions:${userId}`);
      if (cached) {
        try { setAccessList(JSON.parse(cached)); } catch { /* ignore */ }
      }
    }

    if (authLoading) {
      setPermLoading(true);
      return;
    }

    if (isAuthenticated && userId) {
      setPermLoading(true);
      axiosInstance.get(`/user/userPermissions/${userId}`)
        .then((res) => {
          const perms = res?.data?.permissions || [];
          setAccessList(perms);
          try { localStorage.setItem(`permissions:${userId}`, JSON.stringify(perms)); } catch(e) { /* ignore quota errors */ }
        })
        .catch((error) => {
          console.error("Error fetching permissions:", error);
        })
        .finally(() => setPermLoading(false));
    } else {
      // Not authenticated or no user yet; keep loading until state settles
      setPermLoading(true);
    }

    return () => {};
  }, [navigate, ctxUser, isAuthenticated, authLoading]);

  // Robust access check: supports key and label, logs for debugging, and handles missing permissions gracefully
  const hasAccess = useCallback((searchLabelOrKey) => {
    if (!Array.isArray(accessList) || accessList.length === 0) return false;
    const normalized = (str) => (str || "").replace(/\s+/g, " ").trim().toLowerCase();
    return accessList.some(item => (
      ((item.label && normalized(item.label) === normalized(searchLabelOrKey)) ||
       (item.key && normalized(item.key) === normalized(searchLabelOrKey))) && item.isActive
    ));
  }, [accessList]);

  const checkAccess = (searchLabelOrKey, path = "#") => {
    if (hasAccess(searchLabelOrKey)) {
      // Clean up path to avoid double slashes
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      navigate(cleanPath);
    } else {
      toast.error("Sorry, you don’t have access. Contact your administrator for access.");
    }
  }

  const cardsWithAccess = useMemo(() => {
    return DASHBOARD_CARDS.map((card) => {
      const allowed = hasAccess(card.permissionKey) || hasAccess(card.searchLabel);
      return { ...card, allowed };
    });
  }, [hasAccess]);

  const handleCardActivate = (card) => {
    if (!card) return;
    const search = card.permissionKey || card.searchLabel || card.title;
    checkAccess(search, card.path);
  };

  const handleKeyDown = (event, card) => {
    if (!card) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardActivate(card);
    }
  };

  return (
    <>
      <Navbar />
      {/* set bg to a image file */}
      <div className="dash-main-bg bg-cover bg-center">
        {permLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-6 p-10 rounded-lg shadow-lg max-w-5xl mx-auto md:grid-cols-3">
          {cardsWithAccess.map((card) => {
            const cardClasses = card.allowed
              ? "cursor-pointer transition-transform hover:scale-[1.02]"
              : "opacity-60 hover:opacity-80 focus:opacity-80 cursor-not-allowed";

            return (
              <div
                key={card.title}
                role="button"
                tabIndex={0}
                aria-disabled={!card.allowed}
                onClick={() => handleCardActivate(card)}
                onKeyDown={(event) => handleKeyDown(event, card)}
                className={`focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 rounded-xl ${cardClasses}`}
              >
                <Card icon={card.icon} title={card.title} />
              </div>
            );
          })}
        </div>
        )}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 p-3 bg-red-500 text-white rounded-full shadow-lg">
          HELP
        </div>
        {/* Show ChatIcon only if user has FLO_ACCESS or label Flo and isActive */}
        {hasAccess("FLO_ACCESS") || hasAccess("Flo") ? <ChatIcon /> : null}
        {/* Emergency voice agent: saves its transcript for the ER team */}
        {(hasAccess("EMERGENCY") || hasAccess("Emergency")) && (
          <EmergencyAgent />
        )}
      </div>

    </>
  );
};

export default MainDashboard;
